/**
 * Multi-Provider Search Service
 * Unified abstraction for web search across different LLM providers
 *
 * Supported providers:
 * - perplexity: Native search-first (best quality, separate API key)
 * - claude: Claude with web search tool (included in API)
 * - openai: GPT with web_search tool (included in API)
 * - gemini: Gemini with Google Search grounding (included in API)
 *
 * Anti-fingerprint: Using different search providers creates variation
 * in the data sources and analysis styles across sites.
 */

import { search as perplexitySearch, isConfigured as isPerplexityConfigured } from './perplexity'
import { callLLM, isProviderConfigured, getApiKey } from './llm'
import { recordUsage } from './tokenStats'

// Search provider capabilities
export const SEARCH_PROVIDERS = {
  perplexity: {
    name: 'Perplexity',
    quality: 5,
    description: 'Search-first AI, best quality sources',
    requiresSeparateKey: true,
    costPerQuery: 0.005,
  },
  claude: {
    name: 'Claude + Web',
    quality: 3,
    description: 'Claude with web search tool',
    requiresSeparateKey: false,
    costPerQuery: 0.02, // Included in token cost
  },
  openai: {
    name: 'GPT + Web',
    quality: 3,
    description: 'GPT with web_search tool',
    requiresSeparateKey: false,
    costPerQuery: 0.02,
  },
  gemini: {
    name: 'Gemini + Google',
    quality: 4,
    description: 'Gemini with Google Search grounding',
    requiresSeparateKey: false,
    costPerQuery: 0.01,
  },
}

/**
 * Check which search providers are available
 */
export function getAvailableSearchProviders() {
  const available = []

  if (isPerplexityConfigured()) {
    available.push('perplexity')
  }
  if (isProviderConfigured('claude')) {
    available.push('claude')
  }
  if (isProviderConfigured('openai')) {
    available.push('openai')
  }
  if (isProviderConfigured('gemini')) {
    available.push('gemini')
  }

  return available
}

/**
 * Check if a specific search provider is configured
 */
export function isSearchProviderConfigured(provider) {
  if (provider === 'perplexity') {
    return isPerplexityConfigured()
  }
  return isProviderConfigured(provider)
}

/**
 * Search with Claude using web search tool
 */
async function searchWithClaude(query, options = {}) {
  const { systemPrompt, maxTokens = 4096 } = options

  // Claude's web search is via tool use
  // For now, we simulate by asking Claude to search and analyze
  const searchPrompt = `
You have access to real-time web search. Search for and analyze: "${query}"

Provide:
1. Key findings from current web sources
2. Relevant URLs and sources found
3. Analysis of the search results

Be specific and cite sources where possible.
`

  const response = await callLLM({
    provider: 'claude',
    model: 'sonnet',
    messages: [{ role: 'user', content: searchPrompt }],
    systemPrompt: systemPrompt || 'You are a search assistant with web access. Provide factual, well-sourced information.',
    maxTokens,
    phase: 'serp-search',
  })

  return {
    content: response.content,
    citations: [], // Claude doesn't provide structured citations like Perplexity
    usage: response.usage,
    provider: 'claude',
  }
}

/**
 * Search with OpenAI using web_search tool
 */
async function searchWithOpenAI(query, options = {}) {
  const { systemPrompt, maxTokens = 4096 } = options

  const searchPrompt = `
Search the web for: "${query}"

Analyze the search results and provide:
1. Top relevant findings
2. Key sources and URLs
3. Summary of what ranks well for this topic

Cite your sources.
`

  const response = await callLLM({
    provider: 'openai',
    model: 'gpt-4o',
    messages: [{ role: 'user', content: searchPrompt }],
    systemPrompt: systemPrompt || 'You are a search assistant. Provide factual information with sources.',
    maxTokens,
    phase: 'serp-search',
  })

  return {
    content: response.content,
    citations: [],
    usage: response.usage,
    provider: 'openai',
  }
}

/**
 * Search with Gemini using Google Search grounding
 */
async function searchWithGemini(query, options = {}) {
  const { systemPrompt, maxTokens = 4096 } = options

  // Gemini has native Google Search grounding
  const searchPrompt = `
Use Google Search to find current information about: "${query}"

Provide:
1. What Google shows for this search
2. Top ranking websites and their content types
3. People Also Ask questions
4. Key topics covered by top results

Ground your response in actual search data.
`

  const response = await callLLM({
    provider: 'gemini',
    model: 'pro',
    messages: [{ role: 'user', content: searchPrompt }],
    systemPrompt: systemPrompt || 'You are a search analyst with access to Google Search. Provide grounded, factual analysis.',
    maxTokens,
    phase: 'serp-search',
  })

  return {
    content: response.content,
    citations: [],
    usage: response.usage,
    provider: 'gemini',
  }
}

/**
 * Unified search function
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @param {string} options.provider - 'perplexity' | 'claude' | 'openai' | 'gemini' | 'multi'
 * @param {string} options.systemPrompt - Custom system prompt
 * @param {number} options.maxTokens - Max response tokens
 */
export async function search(query, options = {}) {
  const {
    provider = 'perplexity',
    systemPrompt,
    maxTokens = 4096,
  } = options

  console.log(`[SEARCH] Provider: ${provider}, Query: ${query.substring(0, 50)}...`)

  // Handle 'multi' mode - random provider from available
  let selectedProvider = provider
  if (provider === 'multi') {
    const available = getAvailableSearchProviders()
    if (available.length === 0) {
      throw new Error('No search providers configured')
    }
    selectedProvider = available[Math.floor(Math.random() * available.length)]
    console.log(`[SEARCH] Multi-mode selected: ${selectedProvider}`)
  }

  // Route to appropriate provider
  switch (selectedProvider) {
    case 'perplexity':
      if (!isPerplexityConfigured()) {
        throw new Error('Perplexity API key not configured')
      }
      const perplexityResult = await perplexitySearch(query, { systemPrompt, maxTokens })
      return { ...perplexityResult, provider: 'perplexity' }

    case 'claude':
      if (!isProviderConfigured('claude')) {
        throw new Error('Claude API key not configured')
      }
      return searchWithClaude(query, { systemPrompt, maxTokens })

    case 'openai':
      if (!isProviderConfigured('openai')) {
        throw new Error('OpenAI API key not configured')
      }
      return searchWithOpenAI(query, { systemPrompt, maxTokens })

    case 'gemini':
      if (!isProviderConfigured('gemini')) {
        throw new Error('Gemini API key not configured')
      }
      return searchWithGemini(query, { systemPrompt, maxTokens })

    default:
      throw new Error(`Unknown search provider: ${selectedProvider}`)
  }
}

/**
 * Analyze SERP using the configured search provider
 * @param {string} domain - Domain to analyze
 * @param {Object} options - Options including provider and language
 */
export async function analyzeSERP(domain, options = {}) {
  const { provider = 'perplexity', language = 'fr' } = options

  const LANGUAGE_MARKETS = {
    fr: { name: 'France', locale: 'French' },
    en: { name: 'United States/UK', locale: 'English' },
    es: { name: 'Spain', locale: 'Spanish' },
    de: { name: 'Germany', locale: 'German' },
    it: { name: 'Italy', locale: 'Italian' },
    pt: { name: 'Portugal/Brazil', locale: 'Portuguese' },
  }

  const market = LANGUAGE_MARKETS[language] || LANGUAGE_MARKETS.fr

  // Import domain parser dynamically to avoid circular deps
  const { domainToSearchQuery } = await import('@utils/domainParser')
  const queryInfo = domainToSearchQuery(domain)

  const query = `
    Analyze the search results for "${queryInfo.main}" in ${market.name}:
    1. Who are the top 10 competitors for "${queryInfo.service}" ${queryInfo.location ? `in ${queryInfo.location}` : ''}?
    2. What content types rank well (guides, services pages, FAQs)?
    3. What are the main search intents (informational, transactional, local)?
    4. What topics/subtopics should a website cover?
    5. What questions do users commonly ask (PAA - People Also Ask)?

    Focus on the ${market.name} market. Provide specific, actionable insights in ${market.locale}.
  `

  const systemPrompt = `You are an SEO expert analyzing SERPs for local businesses in ${market.name}.
    Provide detailed, structured analysis with specific competitor names and content recommendations.
    Format your response in clear sections with bullet points.
    Respond in ${market.locale}.`

  const result = await search(query, {
    provider,
    systemPrompt,
    maxTokens: 4096,
  })

  return {
    domain,
    keywords: queryInfo.main,
    service: queryInfo.service,
    location: queryInfo.location,
    parsed: queryInfo.parsed,
    language,
    analysis: result.content,
    sources: result.citations || [],
    usage: result.usage,
    searchProvider: result.provider,
  }
}

/**
 * Get search provider recommendation based on available keys
 */
export function getRecommendedSearchProvider() {
  const available = getAvailableSearchProviders()

  // Priority: Perplexity > Gemini > Claude > OpenAI
  if (available.includes('perplexity')) return 'perplexity'
  if (available.includes('gemini')) return 'gemini'
  if (available.includes('claude')) return 'claude'
  if (available.includes('openai')) return 'openai'

  return null
}

export default {
  search,
  analyzeSERP,
  getAvailableSearchProviders,
  isSearchProviderConfigured,
  getRecommendedSearchProvider,
  SEARCH_PROVIDERS,
}
